import { uuid } from '../../../../../util/uuid';
import {
  DropZone,
  LayoutCell,
  LayoutBoxCell,
  LayoutPanelCell,
  TearsheetPanelType,
  TearsheetV2Layout,
} from '../types';

export function generateNodeId(): string {
  return `node-${uuid()}`;
}

// Create a panel cell
export function createPanelCell(panelType: TearsheetPanelType): LayoutPanelCell {
  return {
    id: generateNodeId(),
    type: 'panel',
    panelType,
  };
}

// Create a box cell with initial children
export function createBoxCell(
  direction: 'row' | 'column',
  children: LayoutCell[],
  sizes?: number[]
): LayoutBoxCell {
  const equalSize = 100 / children.length;
  return {
    id: generateNodeId(),
    type: 'box',
    direction,
    children,
    sizes: sizes ?? children.map(() => equalSize),
  };
}

// Find a cell by ID in the layout tree
export function findCell(root: LayoutCell | null, cellId: string): LayoutCell | null {
  if (!root) {
    return null;
  }
  if (root.id === cellId) {
    return root;
  }

  if (root.type !== 'panel') {
    for (const child of root.children) {
      const found = findCell(child, cellId);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

// Find the parent box cell of a given cell
export function findParentCell(
  root: LayoutCell | null,
  cellId: string
): { parent: LayoutBoxCell; index: number } | null {
  if (!root || root.type === 'panel') {
    return null;
  }

  for (let i = 0; i < root.children.length; i++) {
    if (root.children[i].id === cellId) {
      return { parent: root, index: i };
    }
    const found = findParentCell(root.children[i], cellId);
    if (found) {
      return found;
    }
  }
  return null;
}

// Add a panel to the layout based on drop target
export function addPanelToLayout(
  layout: TearsheetV2Layout,
  panelType: TearsheetPanelType,
  targetCellId: string | undefined,
  zone: DropZone
): TearsheetV2Layout {
  const newPanelCell = createPanelCell(panelType);

  // Empty layout - create single panel
  if (!layout.root) {
    return {
      ...layout,
      root: newPanelCell,
    };
  }

  // Canvas-level drop - wrap existing root with new box
  if (!targetCellId) {
    const direction = zone === 'top' || zone === 'bottom' ? 'column' : 'row';
    const children =
      zone === 'top' || zone === 'left' ? [newPanelCell, layout.root] : [layout.root, newPanelCell];

    return {
      ...layout,
      root: createBoxCell(direction, children),
    };
  }

  // Panel-level drop - find target and wrap it
  const newRoot = insertAtCell(layout.root, targetCellId, newPanelCell, zone);
  return {
    ...layout,
    root: newRoot,
  };
}

// Insert a new cell relative to a target cell
function insertAtCell(
  root: LayoutCell,
  targetCellId: string,
  newCell: LayoutCell,
  zone: DropZone
): LayoutCell {
  // Found the target cell
  if (root.id === targetCellId) {
    // Create a box containing both old and new
    const direction = zone === 'top' || zone === 'bottom' ? 'column' : 'row';
    const children = zone === 'top' || zone === 'left' ? [newCell, root] : [root, newCell];
    return createBoxCell(direction, children);
  }

  // Recurse into box cells
  if (root.type !== 'panel') {
    return {
      ...root,
      children: root.children.map((child) => insertAtCell(child, targetCellId, newCell, zone)),
    };
  }

  return root;
}

// Remove a cell from the layout
export function removeCell(layout: TearsheetV2Layout, cellId: string): TearsheetV2Layout {
  if (!layout.root) {
    return layout;
  }

  // Removing the root
  if (layout.root.id === cellId) {
    return { ...layout, root: null };
  }

  const newRoot = removeCellFromTree(layout.root, cellId);
  return { ...layout, root: newRoot };
}

function removeCellFromTree(root: LayoutCell, cellId: string): LayoutCell | null {
  if (root.type === 'panel') {
    return root;
  }

  const newChildren: LayoutCell[] = [];
  const newSizes: number[] = [];
  let removedIndex = -1;
  let removedSize = 0;

  for (let i = 0; i < root.children.length; i++) {
    const child = root.children[i];
    if (child.id === cellId) {
      removedIndex = i;
      removedSize = root.sizes?.[i] ?? 100 / root.children.length;
    } else {
      const processed = removeCellFromTree(child, cellId);
      if (processed) {
        newChildren.push(processed);
        newSizes.push(root.sizes?.[i] ?? 100 / root.children.length);
      } else {
        // Child was removed (was a single-child box)
        removedIndex = i;
        removedSize = root.sizes?.[i] ?? 100 / root.children.length;
      }
    }
  }

  // If we removed a child, redistribute its size
  if (removedIndex !== -1 && newSizes.length > 0) {
    const redistributed = removedSize / newSizes.length;
    for (let i = 0; i < newSizes.length; i++) {
      newSizes[i] += redistributed;
    }
  }

  // If only one child remains, return it directly (collapse the box)
  if (newChildren.length === 1) {
    return newChildren[0];
  }

  // If no children remain, return null
  if (newChildren.length === 0) {
    return null;
  }

  return {
    ...root,
    children: newChildren,
    sizes: newSizes,
  };
}

// Move a panel from one location to another
export function movePanel(
  layout: TearsheetV2Layout,
  sourceCellId: string,
  targetCellId: string,
  zone: DropZone
): TearsheetV2Layout {
  if (!layout.root) {
    return layout;
  }

  // Find the source cell
  const sourceCell = findCell(layout.root, sourceCellId);
  if (!sourceCell || sourceCell.type !== 'panel') {
    return layout;
  }

  // Remove from source location
  const newLayout = removeCell(layout, sourceCellId);

  // If layout became empty, just add the panel back
  if (!newLayout.root) {
    return {
      ...layout,
      root: sourceCell,
    };
  }

  // Insert at target location
  const newRoot = insertAtCell(newLayout.root, targetCellId, sourceCell, zone);
  return { ...newLayout, root: newRoot };
}

// Update sizes for a box cell
export function updateCellSizes(
  layout: TearsheetV2Layout,
  boxCellId: string,
  newSizes: number[]
): TearsheetV2Layout {
  if (!layout.root) {
    return layout;
  }

  const newRoot = updateSizesInTree(layout.root, boxCellId, newSizes);
  return { ...layout, root: newRoot };
}

function updateSizesInTree(root: LayoutCell, targetId: string, newSizes: number[]): LayoutCell {
  if (root.id === targetId && root.type !== 'panel') {
    return { ...root, sizes: newSizes };
  }

  if (root.type !== 'panel') {
    return {
      ...root,
      children: root.children.map((child) => updateSizesInTree(child, targetId, newSizes)),
    };
  }

  return root;
}

// Get all used panel types
export function getUsedPanelTypes(layout: TearsheetV2Layout): Set<TearsheetPanelType> {
  const used = new Set<TearsheetPanelType>();
  if (!layout.root) {
    return used;
  }

  collectPanelTypes(layout.root, used);
  return used;
}

function collectPanelTypes(cell: LayoutCell, set: Set<TearsheetPanelType>): void {
  if (cell.type === 'panel') {
    set.add(cell.panelType);
  } else {
    for (const child of cell.children) {
      collectPanelTypes(child, set);
    }
  }
}
